import { ReportInsights } from '../../types'
import { motion } from 'framer-motion'
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  BarChart3, 
  Target, 
  Lightbulb, 
  Clock, 
  DollarSign, 
  Users, 
  MousePointer, 
  Eye, 
  Smartphone, 
  Monitor, 
  Tablet,
  MapPin,
  Globe,
  Zap
} from 'lucide-react'

interface ReportInsightsTabProps {
  data: ReportInsights
}

export function ReportInsightsTab({ data }: ReportInsightsTabProps) {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading report insights...</p>
          <p className="text-xs text-gray-400 mt-2">Data not available</p>
        </div>
      </div>
    )
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num)
  }

  const formatPercentage = (num: number) => {
    return `${num.toFixed(1)}%`
  }

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-green-500" />
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-500" />
      default:
        return <Minus className="h-4 w-4 text-gray-500" />
    }
  }

  const getTrendColor = (changeType: 'increase' | 'decrease' | 'stable') => {
    switch (changeType) {
      case 'increase':
        return 'text-green-600'
      case 'decrease':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  const getPerformanceColor = (performance: 'excellent' | 'good' | 'average' | 'poor') => {
    switch (performance) {
      case 'excellent':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'good':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'average':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'poor':
        return 'bg-red-100 text-red-800 border-red-200'
    }
  }

  const getPriorityColor = (priority: 'high' | 'medium' | 'low') => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200'
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'optimization':
        return <Zap className="h-4 w-4" />
      case 'budget':
        return <DollarSign className="h-4 w-4" />
      case 'creative':
        return <Target className="h-4 w-4" />
      case 'targeting':
        return <Users className="h-4 w-4" />
      case 'timing':
        return <Clock className="h-4 w-4" />
      default:
        return <Lightbulb className="h-4 w-4" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6"
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Report Insights</h2>
        <p className="text-gray-700">AI-powered analytics and recommendations for campaign optimization</p>
        <div className="mt-2 text-sm text-gray-500">
          Generated on {new Date(data.generatedAt).toLocaleDateString()} at {new Date(data.generatedAt).toLocaleTimeString()}
        </div>
      </motion.div>

      {/* Overview Metrics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm border p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <BarChart3 className="h-5 w-5 mr-2 text-purple-600" />
          Campaign Overview ({data.overview.period})
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <Eye className="h-4 w-4 text-gray-500" />
              <span className="text-xs text-gray-500">Impressions</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatNumber(data.overview.totalImpressions)}</div>
            <div className={`text-sm flex items-center ${getTrendColor(data.performance.impressions.changeType)}`}>
              {getTrendIcon(data.performance.impressions.trend)}
              <span className="ml-1">{Math.abs(data.performance.impressions.change)}%</span>
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <MousePointer className="h-4 w-4 text-gray-500" />
              <span className="text-xs text-gray-500">Clicks</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatNumber(data.overview.totalClicks)}</div>
            <div className={`text-sm flex items-center ${getTrendColor(data.performance.clicks.changeType)}`}>
              {getTrendIcon(data.performance.clicks.trend)}
              <span className="ml-1">{Math.abs(data.performance.clicks.change)}%</span>
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <Target className="h-4 w-4 text-gray-500" />
              <span className="text-xs text-gray-500">Conversions</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatNumber(data.overview.totalConversions)}</div>
            <div className={`text-sm flex items-center ${getTrendColor(data.performance.conversions.changeType)}`}>
              {getTrendIcon(data.performance.conversions.trend)}
              <span className="ml-1">{Math.abs(data.performance.conversions.change)}%</span>
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="h-4 w-4 text-gray-500" />
              <span className="text-xs text-gray-500">Spend</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(data.overview.totalSpend)}</div>
            <div className={`text-sm flex items-center ${getTrendColor(data.performance.spend.changeType)}`}>
              {getTrendIcon(data.performance.spend.trend)}
              <span className="ml-1">{Math.abs(data.performance.spend.change)}%</span>
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-4 w-4 text-gray-500" />
              <span className="text-xs text-gray-500">ROAS</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{data.overview.roas.toFixed(1)}x</div>
            <div className={`text-sm flex items-center ${getTrendColor(data.performance.roas.changeType)}`}>
              {getTrendIcon(data.performance.roas.trend)}
              <span className="ml-1">{Math.abs(data.performance.roas.change)}%</span>
            </div>
          </div>
        </div>

        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-sm text-gray-600">CTR</div>
            <div className="text-xl font-bold text-blue-600">{formatPercentage(data.overview.ctr)}</div>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-sm text-gray-600">CPC</div>
            <div className="text-xl font-bold text-green-600">{formatCurrency(data.overview.cpc)}</div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <div className="text-sm text-gray-600">CPM</div>
            <div className="text-xl font-bold text-purple-600">{formatCurrency(data.overview.cpm)}</div>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <div className="text-sm text-gray-600">Conv. Rate</div>
            <div className="text-xl font-bold text-orange-600">{formatPercentage(data.overview.conversionRate)}</div>
          </div>
        </div>
      </motion.div>

      {/* AI Recommendations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm border p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Lightbulb className="h-5 w-5 mr-2 text-yellow-600" />
          AI Recommendations
        </h3>
        
        <div className="space-y-4">
          {data.recommendations.map((recommendation) => (
            <div key={recommendation.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow duration-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    {getCategoryIcon(recommendation.category)}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{recommendation.title}</h4>
                    <p className="text-sm text-gray-600">{recommendation.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(recommendation.priority)}`}>
                    {recommendation.priority} priority
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(recommendation.impact)}`}>
                    {recommendation.impact} impact
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Expected Improvement:</span>
                  <div className="font-medium text-gray-900">{recommendation.expectedImprovement}</div>
                </div>
                <div>
                  <span className="text-gray-500">Effort Required:</span>
                  <div className="font-medium text-gray-900 capitalize">{recommendation.effort}</div>
                </div>
                <div>
                  <span className="text-gray-500">Action:</span>
                  <div className="font-medium text-gray-900">{recommendation.action}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Top Performing Channels */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-lg shadow-sm border p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
          Top Performing Channels
        </h3>
        
        <div className="space-y-3">
          {data.analytics.topPerformingChannels.map((channel, index) => (
            <div key={index} className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{channel.channel}</h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPerformanceColor(channel.performance)}`}>
                  {channel.performance}
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Impressions</div>
                  <div className="font-medium">{formatNumber(channel.impressions)}</div>
                </div>
                <div>
                  <div className="text-gray-500">CTR</div>
                  <div className="font-medium">{formatPercentage(channel.ctr)}</div>
                </div>
                <div>
                  <div className="text-gray-500">CPC</div>
                  <div className="font-medium">{formatCurrency(channel.cpc)}</div>
                </div>
                <div>
                  <div className="text-gray-500">ROAS</div>
                  <div className="font-medium">{channel.roas.toFixed(1)}x</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Device Breakdown */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-lg shadow-sm border p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Smartphone className="h-5 w-5 mr-2 text-green-600" />
          Device Performance
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.analytics.deviceBreakdown.map((device, index) => {
            const getDeviceIcon = (deviceType: string) => {
              switch (deviceType.toLowerCase()) {
                case 'mobile':
                  return <Smartphone className="h-5 w-5" />
                case 'desktop':
                  return <Monitor className="h-5 w-5" />
                case 'tablet':
                  return <Tablet className="h-5 w-5" />
                default:
                  return <Globe className="h-5 w-5" />
              }
            }
            
            return (
              <div key={index} className="p-4 border rounded-lg text-center">
                <div className="flex items-center justify-center mb-2">
                  {getDeviceIcon(device.device)}
                  <span className="ml-2 font-medium text-gray-900">{device.device}</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">{formatPercentage(device.percentage)}</div>
                <div className="text-sm text-gray-500">of total traffic</div>
                <div className="mt-2 text-xs text-gray-600">
                  CTR: {formatPercentage(device.ctr)} | Conv: {formatPercentage(device.conversionRate)}
                </div>
              </div>
            )
          })}
        </div>
      </motion.div>

      {/* Geographic Performance */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-lg shadow-sm border p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <MapPin className="h-5 w-5 mr-2 text-red-600" />
          Geographic Performance
        </h3>
        
        <div className="space-y-3">
          {data.analytics.geographicPerformance.map((location, index) => (
            <div key={index} className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{location.location}</h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPerformanceColor(location.performance)}`}>
                  {location.performance}
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Impressions</div>
                  <div className="font-medium">{formatNumber(location.impressions)}</div>
                </div>
                <div>
                  <div className="text-gray-500">CTR</div>
                  <div className="font-medium">{formatPercentage(location.ctr)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Spend</div>
                  <div className="font-medium">{formatCurrency(location.spend)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Conv. Rate</div>
                  <div className="font-medium">{formatPercentage(location.conversionRate)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  )
}
